import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/core/services/app.service';
import { ClassInfo, TenantClientService } from 'src/app/core/services/tenant-client.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TenantHistoryService } from 'src/app/core/services/tenant-history.service';

type PendingAction = 'deleteData' | 'deleteTenant' | 'rebuild';
type RecalculateState = 'idle' | 'loading' | 'done' | 'error';

@Component({
    selector: 'app-system',
    templateUrl: './system.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SystemComponent implements OnInit {
  tenantCode: string;
  tenantName: string;
  actionsEnabled = true;
  pendingAction: PendingAction | null = null;
  confirmInput = '';

  showRecalculate = false;
  typesLoading = false;
  showAllTypes = false;
  types: ClassInfo[] = [];
  recalculateStates: Record<string, RecalculateState> = {};
  syncStates: Record<string, RecalculateState> = {};

  constructor(
    private api: TenantClientService,
    private appService: AppService,
    private history: TenantHistoryService,
    private notifications: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.tenantCode = this.route.snapshot.params['tenantCode'];
    this.tenantName = this.appService.tenantSubject.getValue()?.name ?? this.tenantCode;
  }

  get confirmValid(): boolean {
    return this.confirmInput === this.tenantName;
  }

  requestConfirm(action: PendingAction): void {
    this.pendingAction = action;
    this.confirmInput = '';
  }

  cancelConfirm(): void {
    this.pendingAction = null;
    this.confirmInput = '';
  }

  confirm(): void {
    if (!this.confirmValid) return;
    const action = this.pendingAction;
    this.pendingAction = null;
    this.actionsEnabled = false;
    if (action === 'deleteTenant') {
      this.history.removeFromHistory(this.tenantCode);
      this.api.deleteTenant(this.tenantCode, this.tenantName).subscribe(() => this.router.navigate(['/']));
    } else if (action === 'rebuild') {
      this.onRebuild();
    } else {
      this.api.deleteTenantData(this.tenantCode, this.tenantName).subscribe(() => this.router.navigate(['/']));
    }
  }

  onClearCache(): void {
    this.actionsEnabled = false;
    this.api.reloadModel(this.tenantCode).subscribe({
      next: (status) => this.router.navigate(
        status.state === 'LOADED' ? ['/', this.tenantCode] : ['/', this.tenantCode, 'system', 'model']),
      error: (error) => {
        this.actionsEnabled = true;
        this.notifications.showServerError(error, 'model.reload_failed');
      },
    });
  }

  onRebuild(): void {
    this.actionsEnabled = false;
    this.api.rebuild(this.tenantCode).subscribe(() => this.router.navigate(['/', this.tenantCode]));
  }

  onLoadAssets(): void {
    this.actionsEnabled = false;
    this.api.loadAssets(this.tenantCode).subscribe(() => this.router.navigate(['/', this.tenantCode]));
  }

  onLoadDefaultData(): void {
    this.actionsEnabled = false;
    this.api.loadDefaultData(this.tenantCode).subscribe(() => this.router.navigate(['/', this.tenantCode]));
  }

  onOpenRecalculate(): void {
    if (this.showRecalculate) {
      this.showRecalculate = false;
      return;
    }
    this.showRecalculate = true;
    this.loadTypes();
  }

  onToggleAllTypes(): void {
    this.showAllTypes = !this.showAllTypes;
    this.loadTypes();
  }

  private loadTypes(): void {
    this.typesLoading = true;
    this.api.getClasses(this.tenantCode, !this.showAllTypes).subscribe({
      next: (types) => {
        this.types = types;
        this.recalculateStates = {};
        this.syncStates = {};
        types.forEach(t => {
          this.recalculateStates[t.pluralName] = 'idle';
          this.syncStates[t.pluralName] = 'idle';
        });
        this.typesLoading = false;
      },
      error: () => { this.typesLoading = false; },
    });
  }

  onRecalculate(type: ClassInfo): void {
    this.recalculateStates[type.pluralName] = 'loading';
    this.api.recalculate(this.tenantCode, type.pluralName).subscribe({
      next: () => { this.recalculateStates[type.pluralName] = 'done'; },
      error: () => { this.recalculateStates[type.pluralName] = 'error'; },
    });
  }

  onSync(type: ClassInfo): void {
    this.syncStates[type.pluralName] = 'loading';
    this.api.sync(this.tenantCode, type.pluralName).subscribe({
      next: () => { this.syncStates[type.pluralName] = 'done'; },
      error: () => { this.syncStates[type.pluralName] = 'error'; },
    });
  }
}
